import { Fragment, useEffect, useState } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { DashboardPageFormBody, DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import type { TargetingClient } from '../../../../../targeting/api';
import { createCustomTargetingExtGroup } from '../../../../../targeting/customTargetingExtModel';
import type { MadBuyLineItemGeneralForm } from '../../../lineItemsModel';
import { CustomTargetingExtGroup } from './CustomTargetingExtGroup';
import { LogicalOperatorRadio } from './LogicalOperatorRadio';

export function CustomTargetingExtForm({ client }: { client: TargetingClient }) {
  'use no memo';

  const { control, setValue } = useFormContext<MadBuyLineItemGeneralForm>();
  const groups = useFieldArray({ control, name: 'customTargetingExt.groups' });
  const topOperator = useWatch({ control, name: 'customTargetingExt.op' });
  const watchedGroups = useWatch({ control, name: 'customTargetingExt.groups' });
  const preservedRaw = useWatch({ control, name: 'customTargetingExt.preservedRaw' });
  const [initialValue] = useState(() => JSON.stringify({ op: topOperator, groups: watchedGroups }));
  const groupOperatorLabel = dashboardT('madBuy.lineItems.customTargetingGroupOperator');

  useEffect(() => {
    const currentValue = JSON.stringify({ op: topOperator, groups: watchedGroups });
    if (preservedRaw && currentValue !== initialValue) {
      setValue('customTargetingExt.preservedRaw', undefined, { shouldDirty: true });
    }
  }, [initialValue, preservedRaw, setValue, topOperator, watchedGroups]);

  return (
    <DashboardPageFormBody
      title={dashboardT('madBuy.lineItems.customTargeting')}
      description={dashboardT('madBuy.lineItems.customTargetingDescription')}
      width="lg"
    >
      <DashboardPageFormField label={groupOperatorLabel}>
        <Controller
          control={control}
          name="customTargetingExt.op"
          render={({ field }) => (
            <LogicalOperatorRadio
              value={field.value}
              onChange={field.onChange}
              ariaLabel={groupOperatorLabel}
            />
          )}
        />
      </DashboardPageFormField>
      <div className="grid gap-4">
        {groups.fields.map((group, index) => (
          <Fragment key={group.id}>
            {index > 0 ? (
              <div className="text-muted-foreground text-xs font-semibold uppercase">
                {topOperator}
              </div>
            ) : null}
            <CustomTargetingExtGroup
              client={client}
              groupIndex={index}
              onRemove={() => {
                groups.remove(index);
              }}
            />
          </Fragment>
        ))}
        <Button
          type="button"
          variant="outline"
          className="w-fit"
          onClick={() => {
            groups.append(createCustomTargetingExtGroup());
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {dashboardT('madBuy.lineItems.customTargetingAddGroup')}
        </Button>
      </div>
    </DashboardPageFormBody>
  );
}
